import {
  MeetingAttemptResponseV2,
  ProductFeatureV2,
} from '@chilipiper/api-client/src/user-availability'
import {
  PublicRoundRobinSchedulingLink,
  TeamSchedulingApiClientV1,
  TeamSchedulingApiClientV2,
} from '@chilipiper/api-client/src/team-scheduling'
import { HandoffApiClientV1 } from '@chilipiper/api-client/src/handoff'
import { MeetingTypesApiClientV1 } from '@chilipiper/api-client/src/meeting-types'
import { partition } from 'remeda'
import invariant from 'tiny-invariant'
import { LinkResourceEnhanced } from '../types'
import { isGroupSchedulingLinkProductFeature } from '../types'

export const getRoutingIdFromMeetingAttempt = (
  meetingAttempt?: MeetingAttemptResponseV2
): string | undefined => {
  if (
    meetingAttempt?.type !== 'NotFound' &&
    meetingAttempt?.attempt.productFeature.type === 'HandoffRouting'
  ) {
    return meetingAttempt.attempt.productFeature.routingId
  }

  return
}

const getLinkIdFromMeetingAttempt = (meetingAttempt?: MeetingAttemptResponseV2) => {
  if (!meetingAttempt || meetingAttempt?.type === 'NotFound') {
    return
  }

  if (
    meetingAttempt?.attempt.productFeature.type === 'RoundRobinSchedulingLink' ||
    meetingAttempt.attempt.productFeature.type === 'OwnershipSchedulingLink'
  ) {
    return meetingAttempt.attempt.productFeature.linkId
  }
}

export const getHostIdFromMeetingAttemptProductFeature = (
  productFeature?: ProductFeatureV2,
  groupSchedulingLinkHostId?: string
): string | undefined => {
  if (
    productFeature?.type === 'AdminIndividualSchedulingLink' ||
    productFeature?.type === 'PersonalIndividualSchedulingLink'
  ) {
    return productFeature.hostId
  }

  if (isGroupSchedulingLinkProductFeature(productFeature)) {
    if (productFeature?.hostId) {
      return productFeature.hostId
    }

    return groupSchedulingLinkHostId
  }

  return
}

export const getMeetingAttemptLinkResource = async (
  tenantId: string,
  handoffClient: HandoffApiClientV1,
  teamSchedulingClientV1: TeamSchedulingApiClientV1,
  teamSchedulingClientV2: TeamSchedulingApiClientV2,
  meetingTypesClient: MeetingTypesApiClientV1,
  meetingAttempt?: MeetingAttemptResponseV2
): Promise<LinkResourceEnhanced | undefined> => {
  if (!meetingAttempt || meetingAttempt?.type === 'NotFound') {
    return
  }

  const productFeatureType = meetingAttempt.attempt.productFeature.type

  if (productFeatureType === 'HandoffRouting') {
    const routingId = getRoutingIdFromMeetingAttempt(meetingAttempt)
    const routing = routingId
      ? await handoffClient.getPublicRoutingInfo(tenantId, routingId)
      : undefined

    if (routing?.type !== 'PublicRoutingInfoForSelected') {
      return undefined
    }

    return {
      type: 'HandoffRouter',
      workspaceId: routing.workspaceId,
      meetingTypeIds: [routing.selectResponse.meetingTypeId],
      requiredAttendees: [],
      selectedAssignees: routing.selectResponse.selectedAssignees,
      selectedMeetingTypeId: routing.selectResponse.meetingTypeId,
      hostInfo: routing.selectResponse.host,
      primaryGuest: routing.primaryGuest,
    }
  }

  if (productFeatureType === 'OwnershipSchedulingLink') {
    const ownershipSchedulingLinkId = getLinkIdFromMeetingAttempt(meetingAttempt)

    const ownershipSchedulingLink = ownershipSchedulingLinkId
      ? await teamSchedulingClientV2.findAOwnershipSchedulingLinkByOwnershipSchedulingLinkIdPublicV2(
          tenantId,
          ownershipSchedulingLinkId
        )
      : undefined

    if (!ownershipSchedulingLink) {
      return Promise.reject(new Error('Invalid Ownership Scheduling Link'))
    }

    return {
      type: 'OwnershipSchedulingLink',
      workspaceId: ownershipSchedulingLink.workspaceId,
      meetingTypeIds: ownershipSchedulingLink.meetingTypeIds,
      requiredAttendees: ownershipSchedulingLink.alwaysInvitedUsers ?? [],
      bookerSettings: ownershipSchedulingLink.meetingCreatedTarget,
      distributionIds: ownershipSchedulingLink.distributionIds,
      linkId: ownershipSchedulingLink.linkId,
      ownershipSettingsV3: ownershipSchedulingLink.ownershipSettingsV3,
    }
  }

  if (productFeatureType === 'RoundRobinSchedulingLink') {
    const roundRobinLinkId = getLinkIdFromMeetingAttempt(meetingAttempt)

    const roundRobinLink: PublicRoundRobinSchedulingLink | undefined = roundRobinLinkId
      ? await teamSchedulingClientV2.findARoundRobinSchedulingLinkByRoundRobinSchedulingLinkIdPublicV2(
          tenantId,
          roundRobinLinkId
        )
      : undefined

    if (!roundRobinLink) {
      return Promise.reject(new Error('Invalid Round Robin Link'))
    }

    return {
      type: 'RoundRobinSchedulingLink',
      workspaceId: roundRobinLink.workspaceId,
      meetingTypeIds: roundRobinLink.meetingTypeIds,
      requiredAttendees: roundRobinLink.alwaysInvitedUsers ?? [],
      bookerSettings: roundRobinLink.meetingCreatedTarget,
      distributionIds: roundRobinLink.distributionIds,
    }
  }

  if (productFeatureType === 'GroupSchedulingLink') {
    const groupSchedulingLink = await teamSchedulingClientV2
      .findAGroupSchedulingLinkByGroupSchedulingLinkIdPublicV2(
        tenantId,
        meetingAttempt.attempt.productFeature.linkId
      )
      .then(result => {
        // Adding the host to the additional invited users to avoid the host being excluded from the required attendees
        // and to avoid the host not being overridden by attendees while scheduling a meeting when host is optional
        return Promise.resolve({
          ...result,
          additionalInvitedUsers: [...(result.additionalInvitedUsers ?? []), result.host],
        })
      })
    const groupSchedulingLinkHostId = getHostIdFromMeetingAttemptProductFeature(
      meetingAttempt.attempt.productFeature,
      groupSchedulingLink.host.userId
    )

    invariant(groupSchedulingLinkHostId, 'Group Schedule Link - Host id not found')

    const [requiredAttendees, optionalAttendees] = partition(
      groupSchedulingLink.additionalInvitedUsers ?? [],
      user => !user.optional
    )

    return {
      type: 'GroupSchedulingLink',
      workspaceId: groupSchedulingLink.workspaceId,
      meetingTypeIds: groupSchedulingLink.meetingTypeIds,
      requiredAttendees: requiredAttendees ?? [],
      optionalAttendees: optionalAttendees ?? [],
      hostId: groupSchedulingLinkHostId,
      isHostOptional:
        groupSchedulingLinkHostId === groupSchedulingLink.host.userId &&
        groupSchedulingLink.host.optional,
    }
  }

  if (productFeatureType === 'AdminIndividualSchedulingLink') {
    const adminSchedulingLink =
      await teamSchedulingClientV1.findAAdminSchedulingLinkByAdminSchedulingLinkIdPublic(
        tenantId,
        meetingAttempt.attempt.productFeature.linkId
      )

    return {
      type: 'AdminSchedulingLink',
      workspaceId: adminSchedulingLink.workspaceId,
      meetingTypeIds: adminSchedulingLink.meetingTypeIds,
      requiredAttendees: [],
    }
  }

  if (productFeatureType === 'PersonalIndividualSchedulingLink') {
    const meetingTypeId = meetingAttempt.attempt.productFeature.meetingTypeId
    const meetingType = await meetingTypesClient
      .publicFindMeetingTypes(tenantId, { id: [meetingTypeId] })
      .then(result => result[meetingTypeId])

    return {
      type: 'PersonalSchedulingLink',
      workspaceId: meetingType.workspaceId,
      meetingTypeIds: [meetingType.id],
      requiredAttendees: [],
    }
  }
}
